'use client';

/**
 * Docs search overlay — the command-palette successor to the old
 * DocsExplorer filter grid. Opened from the sidebar search button or
 * Cmd/Ctrl-K anywhere on the docs pages. Filters title + summary live
 * (cmdk value + keywords), results grouped by the story-order sidebar
 * sections; Enter / click navigates to the doc.
 *
 * Client component over plain serialized nav data only — never imports
 * server-only packages (feedback_client_imports_from_server_packages.md).
 */
import { faFileLines } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@repo/ui/components/ui/command';
import { useRouter } from 'next/navigation';
import type { DocsNavGroup } from '../_lib/docs-nav';

export function DocsSearchDialog({
  nav,
  open,
  onOpenChange,
}: {
  nav: DocsNavGroup[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const router = useRouter();

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} label="Search docs">
      <CommandInput placeholder="Search the docs…" />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>No docs match your search.</CommandEmpty>
        {nav.map((group) => (
          <CommandGroup key={group.id} heading={group.label}>
            {group.items.map((item) => (
              <CommandItem
                key={item.slug}
                value={`${item.title} ${item.slug}`}
                keywords={[item.summary]}
                onSelect={() => {
                  onOpenChange(false);
                  router.push(`/docs/${item.slug}`);
                }}
              >
                <FontAwesomeIcon icon={faFileLines} className="mr-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{item.title}</span>
                  <span className="truncate text-xs text-muted-foreground">{item.summary}</span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
